/* eslint-disable lingui/no-unlocalized-strings */
// Terms of service — port of design/source/public/terms.html.
import { t } from "@lingui/macro";
import { Link } from "react-router";

import type { LegalSection } from "@/client/components/public/legal-layout";
import { LegalLayout } from "@/client/components/public/legal-layout";
import { SEO } from "@/client/components/seo";

const SECTIONS: LegalSection[] = [
  {
    id: "agreement",
    title: "1. The agreement",
    body: (
      <>
        <p>
          These Terms (&quot;Terms&quot;) govern your use of Apply Edge (the &quot;Service&quot;),
          operated by Apply Edge (entity formation in progress) (&quot;we&quot;, &quot;us&quot;),
          with registered address pending in Dubai, UAE. By creating an account or using the
          Service, you agree to these Terms and to our{" "}
          <Link to="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
        <p>
          If you&apos;re using Apply Edge on behalf of an organization, you represent that you have
          authority to bind that organization to these Terms.
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    title: "2. Eligibility",
    body: (
      <p>
        You must be at least 16 years old and legally able to enter contracts in your jurisdiction.
        Apply Edge is not designed for users in countries subject to comprehensive US, UK, EU, or UN
        sanctions.
      </p>
    ),
  },
  {
    id: "account",
    title: "3. Your account",
    body: (
      <ul className="list-disc space-y-2 pl-5">
        <li>
          You&apos;re responsible for maintaining the confidentiality of your account credentials
          and for all activity under your account.
        </li>
        <li>
          One person, one account. Sharing logins isn&apos;t allowed; team or family plans (when
          introduced) will support multi-seat properly.
        </li>
        <li>
          You must give us accurate information and keep it updated — particularly your email, since
          that&apos;s how we reach you for billing and security events.
        </li>
        <li>
          If you suspect unauthorized access, write to{" "}
          <a className="text-primary hover:underline" href="mailto:security@apply-edge.com">
            security@apply-edge.com
          </a>{" "}
          immediately.
        </li>
      </ul>
    ),
  },
  {
    id: "use",
    title: "4. Acceptable use",
    body: (
      <>
        <p>Don&apos;t use Apply Edge to:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Apply to jobs on behalf of someone else without their explicit consent.</li>
          <li>Submit knowingly false claims about your experience, education, or identity.</li>
          <li>
            Send spam, mass-blast unrelated job boards, or violate the terms of any job site we
            integrate with.
          </li>
          <li>
            Reverse-engineer, crawl, or attempt to extract our model prompts, embeddings, or
            proprietary data.
          </li>
          <li>
            Use the Service to harass, defame, or discriminate against any individual or
            organization.
          </li>
          <li>
            Resell, sublicense, or expose Apply Edge functionality through your own product without
            a written commercial agreement.
          </li>
        </ul>
        <p>
          We may suspend or terminate accounts that violate these rules, with refund only if the
          violation is unintentional and you cooperate with correction.
        </p>
      </>
    ),
  },
  {
    id: "ai",
    title: "5. AI features",
    body: (
      <>
        <p>
          Apply Edge generates resumes, cover letters, and other content using large language
          models. You agree that:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            You will <strong>review and edit</strong> AI-generated content before submitting it. The
            Service is an assistant, not a substitute for your judgment.
          </li>
          <li>
            You are responsible for the accuracy of all content you send to employers — even if AI
            helped draft it.
          </li>
          <li>
            AI output may occasionally be wrong, biased, or repetitive. We work to reduce this; we
            don&apos;t guarantee perfection.
          </li>
          <li>
            You retain ownership of your inputs and outputs (Section 7), but you grant us the
            limited license needed to operate the Service.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "billing",
    title: "6. Billing & subscriptions",
    body: (
      <ul className="list-disc space-y-2 pl-5">
        <li>
          Paid plans renew automatically at the end of each billing period at the then-current rate.
          You can cancel anytime in Settings → Billing; cancellation takes effect at the end of the
          current period.
        </li>
        <li>
          We may change prices on existing subscribers with at least 30 days&apos; notice; you can
          cancel before the increase takes effect.
        </li>
        <li>
          Promotional discounts apply only to the introductory term unless stated otherwise.
          Renewals revert to standard rates.
        </li>
        <li>
          Refunds are governed by our{" "}
          <Link to="/refund-policy" className="text-primary hover:underline">
            Refund Policy
          </Link>
          .
        </li>
        <li>
          If a payment fails, we&apos;ll retry over 14 days. Persistent failure downgrades you to
          Free; your data is preserved.
        </li>
      </ul>
    ),
  },
  {
    id: "ip",
    title: "7. Intellectual property",
    body: (
      <>
        <p>
          <strong>Your content stays yours.</strong> Resumes, cover letters, application notes, and
          any text you write remain your property.
        </p>
        <p>
          <strong>We get a limited license</strong> to host, store, transmit, display, modify (e.g.
          format conversion), and process your content solely to provide the Service to you.
        </p>
        <p>
          <strong>Our IP is ours.</strong> The Service, including its design, source code, prompts,
          models, branding, and documentation, is owned by Apply Edge (entity formation in progress)
          and protected by international copyright and trademark law.
        </p>
        <p>
          <strong>Feedback.</strong> If you send us feedback or suggestions, you grant us a
          perpetual, royalty-free license to use it without restriction.
        </p>
      </>
    ),
  },
  {
    id: "termination",
    title: "8. Termination",
    body: (
      <>
        <p>
          You can delete your account anytime in Settings → Privacy. We can suspend or terminate
          access if you breach these Terms, with notice unless the breach is severe (fraud, illegal
          activity, security risk).
        </p>
        <p>
          On termination, your right to use the Service ends. We&apos;ll preserve your exported data
          on request for up to 30 days post-deletion in case you change your mind, then permanently
          delete it (see Privacy Policy §5).
        </p>
      </>
    ),
  },
  {
    id: "warranty",
    title: "9. Disclaimers",
    body: (
      <>
        <p className="font-mono text-[12.5px] leading-[1.65] text-foreground/75">
          THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; WE DISCLAIM ALL
          WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, AND NON-INFRINGEMENT, TO THE MAXIMUM EXTENT PERMITTED BY LAW.
        </p>
        <p>
          We don&apos;t guarantee that Apply Assist will land you a job, that AI-generated content
          will be free of errors, or that integrations with external job boards will always work —
          those depend on partners we don&apos;t control.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    title: "10. Limitation of liability",
    body: (
      <>
        <p>
          To the maximum extent permitted by law, Apply Edge (entity formation in progress) will not
          be liable for indirect, incidental, special, consequential, or exemplary damages,
          including lost profits, lost opportunities, or data loss, arising out of or related to
          your use of the Service.
        </p>
        <p>
          Our aggregate liability for any claim relating to the Service is limited to the greater of
          (a) the amount you paid us in the 12 months before the event giving rise to the claim, or
          (b) USD 100.
        </p>
        <p>
          Some jurisdictions don&apos;t allow these limitations. In that case, they apply to the
          fullest extent permitted.
        </p>
      </>
    ),
  },
  {
    id: "indemnity",
    title: "11. Indemnification",
    body: (
      <p>
        You&apos;ll indemnify and hold harmless Apply Edge (entity formation in progress) and its
        team from claims arising out of (a) content you submit, (b) your use of the Service in
        breach of these Terms, or (c) your violation of any law or third-party right.
      </p>
    ),
  },
  {
    id: "changes",
    title: "12. Changes to the Terms",
    body: (
      <p>
        We may update these Terms. Material changes are emailed to active users at least 30 days
        before they take effect. Continued use after the effective date means you accept the new
        Terms; if you don&apos;t, cancel before they take effect.
      </p>
    ),
  },
  {
    id: "law",
    title: "13. Governing law & disputes",
    body: (
      <p>
        Governing law: TBD pending entity formation. By using the service you agree to comply with
        applicable law in your jurisdiction.
      </p>
    ),
  },
  {
    id: "misc",
    title: "14. Miscellaneous",
    body: (
      <ul className="list-disc space-y-2 pl-5">
        <li>
          <strong>Entire agreement.</strong> These Terms plus the Privacy Policy and Refund Policy
          form the full agreement between you and us.
        </li>
        <li>
          <strong>Severability.</strong> If any clause is unenforceable, the rest stays in effect.
        </li>
        <li>
          <strong>No waiver.</strong> Our not enforcing a right doesn&apos;t waive it.
        </li>
        <li>
          <strong>Assignment.</strong> You can&apos;t assign these Terms; we can, in connection with
          a merger, acquisition, or sale of assets.
        </li>
        <li>
          <strong>Notices.</strong> We&apos;ll notify you by email or in-product banner. You can
          reach us at{" "}
          <a className="text-primary hover:underline" href="mailto:legal@apply-edge.com">
            legal@apply-edge.com
          </a>
          .
        </li>
      </ul>
    ),
  },
];

export const TermsPage = () => (
  <div className="min-h-screen delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
    <SEO
      title={t`Terms of service — Apply Edge`}
      description={t`Apply Edge terms of service. Use the product honestly, don't impersonate other people, review what AI writes for you, pay for what you use.`}
      canonical="/terms"
    />
    <LegalLayout
      titleHead="Terms of"
      titleEmph="service"
      meta="Last updated April 27, 2026 · Effective May 11, 2026"
      summary={{
        label: "In one breath",
        body: "Use Apply Edge honestly, don't impersonate other people, review what AI writes for you, pay for what you use. Cancel any time.",
      }}
      sections={SECTIONS}
    />
  </div>
);
